import { SiteHeader } from "@/components/ui-kit"

import { useEnrichedContracts } from "@/hooks/useEnrichedContracts"
import { useEffect, useMemo, useState } from "react"
import { ContractsGroup } from "@/components/contracts/contracts-group"
import { CreditCardIcon, InvoiceIcon, LockIcon } from "@/components/icons"
import { Button as InitialButton } from "@/components/ui/button"
import { Facility } from "@/types/warehouse"
import { ContractsCard } from "@/components/contracts/contracts-card"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { usePinCodes } from "@/hooks/guard/usePinCodes"
import {
  AdvancePaySuccessDialog,
  type AdvancePayNavigationState,
} from "@/components/contracts/modals/advance-pay-success-dialog"

import { Card } from "@/components/ui-kit/card"
import { Button } from "@/components/ui-kit/button"
import { ArrowRightIcon } from "@/components/icons"
import { useIsMobile } from "@/hooks/useIsMobile"
import { Loader2 } from "lucide-react"

export function HomePage() {
  const { contracts, isLoading, failed, retry } = useEnrichedContracts()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: pinCodes } = usePinCodes()
  const [isAdvancePaySuccessOpen, setIsAdvancePaySuccessOpen] = useState(
    () => Boolean((location.state as AdvancePayNavigationState | null)?.showAdvancePaySuccess),
  )
  const isMobile = useIsMobile()
  useEffect(() => {
    if ((location.state as AdvancePayNavigationState | null)?.showAdvancePaySuccess) {
      navigate(location.pathname, { replace: true })
    }
  }, [location.pathname, location.state, navigate])
  const groupedContracts = useMemo(() => {
    return contracts.reduce<Record<string, typeof contracts>>((acc, contract) => {
      const facilityCode = contract.facility

      if (!acc[facilityCode]) {
        acc[facilityCode] = []
      }

      acc[facilityCode].push(contract)
      return acc
    }, {})
  }, [contracts])

  const lockedContracts = contracts.filter(contract => contract.status === 'locked')
  const lockedAddresses = Array.from(new Set(lockedContracts.map(contract => contract.facilityItem?.properties?.find(property => property.code === 'display_name')?.value)))
  const lockedUnits = Array.from(new Set(lockedContracts.map(contract => contract.unitItem?.code ?? contract.unit)))

  return (
    <div className="min-h-screen bg-background sm:pt-20 pt-16">
      <SiteHeader />
      
      <main className="mx-auto w-full sm:max-w-[722px] max-w-full px-2 sm:pt-8 pt-4 pb-16 sm:px-4">
        <section>
          {isLoading ? (
            <div role="status" aria-label="Загружаем боксы" className="flex justify-center py-16">
              <Loader2 className="size-8 animate-spin text-brand" aria-hidden />
            </div>
          ) : failed ? (
            <div className="flex flex-col gap-[24px] mb-[48px]">
              <h1 className="text-4xl leading-tight font-semibold text-foreground md:text-5xl">
                {t('contracts.loadFailed')}
              </h1>
              <Button variant="secondary" onClick={retry}>
                {t('contracts.retry')}
              </Button>
            </div>
          ) : contracts.length != 0 ? (
            <>
            {lockedContracts.length > 0 ? (
              <div className="flex flex-col sm:gap-[24px] gap-2 sm:mb-[48px] mb-8">
                <Card className="sm:mt-10 sm:rounded-xl rounded-[12px] bg-card sm:p-6 px-[25px] py-[32px] text-center">
                  <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full bg-[#df0f0f] text-white">
                    <LockIcon size={32} className="size-[32px]" />
                  </div>
                  <h2 className="mb-3 sm:text-2xl text-[24px] leading-[30px] font-semibold text-[#df0f0f]">
                  {t('contracts.lockedTitle', { address: lockedAddresses.join(', \n') })}
                  </h2>
                  <p className="mx-auto sm:mb-6 max-w-[420px] sm:text-lg text-[16px] sm:leading-6 leading-[22px] text-muted-foreground">
                  {t('contracts.lockedDescription', { unit: lockedUnits.join(', ') })}
                  </p>
                </Card>

                {lockedContracts.map(contract => (
                  <ContractsCard key={contract.id} contract={contract} props={{ isGroupLocked: true }} />
                ))}
              </div>
            ) : (
              <div className="sm:mb-10 mb-6 border-b border-border sm:pb-8 pb-3 px-4 sm:px-0">
                <h1 className="sm:mb-6 mb-2 text-[28px] leading-[36px] sm:text-[48px] sm:leading-[52px] font-semibold text-foreground">
                  {t('contracts.boxesCount', { count: contracts.length })}
                </h1>
                <div className="flex flex-wrap gap-6 sm:justify-start justify-between">
                  <InitialButton
                    variant="link"
                    className="cursor-pointer flex items-center gap-2 sm:text-lg text-[16px] sm:leading-6 leading-[22px] text-muted-foreground transition-colors hover:text-brand px-0"
                    onClick={() => navigate('/advance-pay')}
                  >
                    <CreditCardIcon size={isMobile ? 18 : 20} className={isMobile ? "size-[18px]" : "size-[20px]"}/>
                    <span>
                      Оплатить заранее
                    </span>
                  </InitialButton>
                  <InitialButton
                    variant="link"
                    className="cursor-pointer flex items-center gap-2 sm:text-lg text-[16px] sm:leading-6 leading-[22px] text-muted-foreground transition-colors hover:text-brand px-0"
                    onClick={() => navigate('/invoices')}
                  >
                    <InvoiceIcon size={isMobile ? 18 : 20} className={isMobile ? "size-[18px]" : "size-[20px]"}/>
                    <span>
                      Архив счетов
                    </span>
                  </InitialButton>
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="flex flex-col gap-[24px] mb-[48px]">
              <h1 className="mb-6 text-4xl leading-tight font-semibold text-foreground md:text-5xl">
                {t('contracts.noContracts')}
              </h1>
              <Button variant="primary" endIcon={<ArrowRightIcon size={24} />} onClick={() => navigate('/rent')}>
                Арендовать бокс до 36 м²
              </Button>
            </div>
          )}

          {!isLoading && !failed && Object.entries(groupedContracts).map(([, contracts]) => (
            <ContractsGroup key={contracts[0].facility} facility={contracts[0].facilityItem as Facility} contracts={contracts} pinCode={pinCodes?.items?.find(pinCode => pinCode.facilityCode === contracts[0].facility)} />
          ))}
        </section>

        
      </main>

      <AdvancePaySuccessDialog
        open={isAdvancePaySuccessOpen}
        onOpenChange={setIsAdvancePaySuccessOpen}
      />
    </div>
  )
}
